import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  RecordVoiceOver as TranscriptIcon,
  CloudUpload as UploadIcon,
  UploadFile as BatchUploadIcon,
  CheckCircleOutline as AcceptIcon,
} from '@mui/icons-material';

import { transcriptsApi, calendarApi } from '../api/services';
import type {
  MeetingTranscript,
  TranscriptExtraction,
  CalendarEvent,
} from '../types/models';
import { transcriptsCopy } from '../copy/transcripts';
import ExtractionCard from '../components/transcripts/ExtractionCard';
import AssignDialog from '../components/transcripts/AssignDialog';

const STATUS_STYLES: Record<string, { bg: string; color: string }> = {
  Uploaded: { bg: 'rgba(136, 146, 168, 0.15)', color: '#8892A8' },
  Processing: { bg: 'rgba(108, 155, 255, 0.15)', color: '#6C9BFF' },
  Analysed: { bg: 'rgba(105, 240, 174, 0.15)', color: '#69F0AE' },
  Accepted: { bg: 'rgba(105, 240, 174, 0.15)', color: '#69F0AE' },
  Error: { bg: 'rgba(255, 82, 82, 0.15)', color: '#FF5252' },
};

// ---------------------------------------------------------------------------
// Upload form — left in place from the previous iteration. Keeping it stable
// while the review surface changes means nobody's existing upload muscle
// memory breaks.
// ---------------------------------------------------------------------------

interface UploadFormProps {
  onUploaded: () => Promise<void>;
}

function UploadForm({ onUploaded }: UploadFormProps) {
  const [title, setTitle] = useState('');
  const [meetingDate, setMeetingDate] = useState('');
  const [attendees, setAttendees] = useState('');
  const [speakerLabels, setSpeakerLabels] = useState('');
  const [transcriptText, setTranscriptText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [autoCreateItems, setAutoCreateItems] = useState(false);
  const [autoCreateSuccess, setAutoCreateSuccess] = useState<string | null>(null);

  const [batchMode, setBatchMode] = useState(false);
  const [batchFiles, setBatchFiles] = useState<File[]>([]);
  const [batchProgress, setBatchProgress] = useState(0);

  const [matchingEvents, setMatchingEvents] = useState<CalendarEvent[]>([]);
  const [recentEvents, setRecentEvents] = useState<CalendarEvent[]>([]);
  const [selectedEventId, setSelectedEventId] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const fmt = (d: Date) => d.toISOString().split('T')[0];
        const events = await calendarApi.getByRange(fmt(yesterday), fmt(today));
        setRecentEvents(events);
      } catch {
        setRecentEvents([]);
      }
    })();
  }, []);

  useEffect(() => {
    if (!meetingDate) {
      setMatchingEvents([]);
      setSelectedEventId('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const events = await calendarApi.getByDate(meetingDate);
        if (!cancelled) {
          setMatchingEvents(events);
          if (events.length > 0 && !title.trim()) {
            setTitle(events[0].title);
            setAttendees(events[0].attendees || '');
            setSelectedEventId(events[0].id);
          }
        }
      } catch {
        if (!cancelled) setMatchingEvents([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [meetingDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEventLink = (eventId: string) => {
    setSelectedEventId(eventId);
    const event =
      matchingEvents.find((e) => e.id === eventId) ||
      recentEvents.find((e) => e.id === eventId);
    if (event) {
      setTitle(event.title);
      setAttendees(event.attendees || '');
    }
  };

  const handleBatchUpload = async () => {
    if (batchFiles.length === 0) return;
    setUploading(true);
    setUploadError(null);
    setUploadSuccess(null);
    setBatchProgress(0);

    let successCount = 0;
    let errorCount = 0;
    for (let i = 0; i < batchFiles.length; i++) {
      try {
        const file = batchFiles[i];
        const text = await file.text();
        const fileTitle = file.name.replace(/\.[^.]+$/, '');
        const transcript = await transcriptsApi.upload({
          title: fileTitle,
          meetingDate: new Date().toISOString().split('T')[0],
          transcriptText: text,
        });
        await transcriptsApi.analyse(transcript.id);
        successCount++;
      } catch {
        errorCount++;
      }
      setBatchProgress(Math.round(((i + 1) / batchFiles.length) * 100));
    }

    if (errorCount > 0) {
      setUploadError(`${errorCount} of ${batchFiles.length} files failed to upload.`);
    }
    if (successCount > 0) {
      setUploadSuccess(
        `${successCount} transcript${successCount === 1 ? '' : 's'} uploaded and analysed.`,
      );
    }
    setBatchFiles([]);
    setBatchProgress(0);
    setUploading(false);
    await onUploaded();
  };

  const handleUploadAndAnalyse = async () => {
    if (!title.trim() || !meetingDate || !transcriptText.trim()) return;

    setUploading(true);
    setUploadError(null);
    setUploadSuccess(null);
    setAutoCreateSuccess(null);

    try {
      const transcript = await transcriptsApi.upload({
        title: title.trim(),
        meetingDate,
        attendees: attendees.trim() || undefined,
        transcriptText: transcriptText.trim(),
        calendarEventId: selectedEventId || undefined,
      });

      if (autoCreateItems) {
        const result = await transcriptsApi.autoAccept(transcript.id);
        setAutoCreateSuccess(
          `Created ${result.workItemsCreated} work item${result.workItemsCreated === 1 ? '' : 's'} from transcript.`,
        );
        setUploadSuccess('Transcript uploaded, analysed, and work items created automatically.');
      } else {
        await transcriptsApi.analyse(transcript.id);
        setUploadSuccess('Transcript uploaded and analysed.');
      }

      setTitle('');
      setMeetingDate('');
      setAttendees('');
      setSpeakerLabels('');
      setTranscriptText('');
      setSelectedEventId('');

      await onUploaded();
    } catch (err: any) {
      setUploadError(err.response?.data?.message || err.message || 'Failed to upload transcript.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
        >
          <Typography variant="h6">Upload transcript</Typography>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={batchMode}
                onChange={(e) => setBatchMode(e.target.checked)}
                disabled={uploading}
              />
            }
            label={<Typography variant="body2">Batch mode</Typography>}
          />
        </Box>

        {uploadError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError(null)}>
            {uploadError}
          </Alert>
        )}
        {uploadSuccess && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setUploadSuccess(null)}>
            {uploadSuccess}
          </Alert>
        )}
        {autoCreateSuccess && (
          <Alert severity="info" sx={{ mb: 2 }} onClose={() => setAutoCreateSuccess(null)}>
            {autoCreateSuccess}
          </Alert>
        )}

        {batchMode ? (
          <Stack spacing={2.5}>
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              Select multiple .txt or .md files. Each file name becomes the transcript title and
              today becomes the meeting date.
            </Alert>
            <Button
              variant="outlined"
              component="label"
              startIcon={<BatchUploadIcon />}
              disabled={uploading}
            >
              {batchFiles.length > 0 ? `${batchFiles.length} file(s) selected` : 'Choose files'}
              <input
                type="file"
                multiple
                accept=".txt,.md"
                hidden
                onChange={(e) => setBatchFiles(Array.from(e.target.files || []))}
              />
            </Button>
            {batchFiles.length > 0 && (
              <Stack spacing={0.5}>
                {batchFiles.map((f, i) => (
                  <Typography key={i} variant="body2" color="text.secondary">
                    {f.name} ({(f.size / 1024).toFixed(1)} KB)
                  </Typography>
                ))}
              </Stack>
            )}
            {uploading && batchProgress > 0 && (
              <Box>
                <LinearProgress variant="determinate" value={batchProgress} sx={{ borderRadius: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  {batchProgress}% complete
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <UploadIcon />}
                onClick={handleBatchUpload}
                disabled={uploading || batchFiles.length === 0}
                size="large"
                sx={{ minWidth: 200 }}
              >
                {uploading
                  ? `Uploading ${batchFiles.length} files…`
                  : `Upload and analyse (${batchFiles.length})`}
              </Button>
            </Box>
          </Stack>
        ) : (
          <Stack spacing={2.5}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                required
                disabled={uploading}
                placeholder="e.g. Weekly leadership sync"
              />
              <TextField
                label="Meeting date"
                type="date"
                value={meetingDate}
                onChange={(e) => setMeetingDate(e.target.value)}
                fullWidth
                required
                disabled={uploading}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Stack>

            {(() => {
              const seen = new Set<string>();
              const allEvents: CalendarEvent[] = [];
              for (const evt of matchingEvents) {
                if (!seen.has(evt.id)) {
                  seen.add(evt.id);
                  allEvents.push(evt);
                }
              }
              for (const evt of recentEvents) {
                if (!seen.has(evt.id)) {
                  seen.add(evt.id);
                  allEvents.push(evt);
                }
              }
              return allEvents.length > 0 ? (
                <FormControl fullWidth>
                  <Select
                    value={selectedEventId}
                    onChange={(e) => handleEventLink(e.target.value)}
                    displayEmpty
                    size="small"
                    disabled={uploading}
                  >
                    <MenuItem value="">
                      <em>Link to meeting (optional)</em>
                    </MenuItem>
                    {allEvents.map((evt) => (
                      <MenuItem key={evt.id} value={evt.id}>
                        {new Date(evt.startTime).toLocaleTimeString('en-AU', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}{' '}
                        — {evt.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null;
            })()}

            <TextField
              label="Attendees"
              value={attendees}
              onChange={(e) => setAttendees(e.target.value)}
              fullWidth
              disabled={uploading}
              placeholder="e.g. John Smith, Jane Doe, Mark Wilson"
              helperText="Optional, comma-separated names"
            />
            <TextField
              label="Speaker labels"
              value={speakerLabels}
              onChange={(e) => setSpeakerLabels(e.target.value)}
              fullWidth
              disabled={uploading}
              placeholder="e.g. Speaker 1: John, Speaker 2: Jane"
              helperText="Optional, helps the AI attribute action items"
            />
            <TextField
              label="Transcript text"
              value={transcriptText}
              onChange={(e) => setTranscriptText(e.target.value)}
              fullWidth
              required
              multiline
              rows={8}
              disabled={uploading}
              placeholder="Paste the full meeting transcript here…"
            />

            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={autoCreateItems}
                    onChange={(e) => setAutoCreateItems(e.target.checked)}
                    disabled={uploading}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2">
                    Auto-create work items (skip review)
                  </Typography>
                }
              />
              <Button
                variant="contained"
                startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <UploadIcon />}
                onClick={handleUploadAndAnalyse}
                disabled={uploading || !title.trim() || !meetingDate || !transcriptText.trim()}
                size="large"
                sx={{ minWidth: 200 }}
              >
                {uploading
                  ? autoCreateItems
                    ? 'Auto-creating work items…'
                    : 'Analysing transcript…'
                  : autoCreateItems
                  ? 'Upload and auto-create'
                  : 'Upload and analyse'}
              </Button>
            </Box>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// TranscriptCard — one past transcript, with tabs that lead on extractions.
// The transcript text itself lives behind a second tab so the review surface
// is always the default view.
// ---------------------------------------------------------------------------

interface TranscriptCardProps {
  transcript: MeetingTranscript;
  onChanged: () => Promise<void>;
}

function TranscriptCard({ transcript, onChanged }: TranscriptCardProps) {
  const [tab, setTab] = useState<'extractions' | 'transcript'>('extractions');
  const [showDismissed, setShowDismissed] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [mutating, setMutating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Assign dialog state — tracks which single extraction the dialog is for.
  const [assignTargetId, setAssignTargetId] = useState<string | null>(null);

  const extractions = useMemo(
    () => transcript.extractions || [],
    [transcript.extractions],
  );
  const isAnalysed = transcript.status === 'Analysed' || transcript.status === 'Accepted';
  const statusStyle = STATUS_STYLES[transcript.status] || STATUS_STYLES.Uploaded;

  // Filter — hide dismissed unless toggled, hide accepted only if every item
  // is handled (so the user always sees at least something post-accept).
  const visible = useMemo(() => {
    return extractions.filter((e) => showDismissed || !e.wasDismissed);
  }, [extractions, showDismissed]);

  const pending = useMemo(
    () => visible.filter((e) => !e.wasAccepted && !e.wasDismissed),
    [visible],
  );
  const hasPending = pending.length > 0;
  const dismissedCount = extractions.filter((e) => e.wasDismissed).length;

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const runAction = async (fn: () => Promise<void>) => {
    setMutating(true);
    setError(null);
    try {
      await fn();
      await onChanged();
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? 'Action failed.');
    } finally {
      setMutating(false);
    }
  };

  const handleAcceptOne = (ext: TranscriptExtraction) =>
    runAction(async () => {
      await transcriptsApi.accept(transcript.id, [ext.id]);
      setSuccess(`Accepted "${ext.title}" into the plan.`);
      setSelected((s) => {
        const next = new Set(s);
        next.delete(ext.id);
        return next;
      });
    });

  const handleDismissOne = (ext: TranscriptExtraction) =>
    runAction(async () => {
      await transcriptsApi.dismiss(transcript.id, [ext.id]);
      setSuccess(`Dismissed "${ext.title}".`);
      setSelected((s) => {
        const next = new Set(s);
        next.delete(ext.id);
        return next;
      });
    });

  const handleAcceptAll = () =>
    runAction(async () => {
      const ids = pending.map((e) => e.id);
      if (ids.length === 0) return;
      await transcriptsApi.accept(transcript.id, ids);
      setSuccess(
        `Accepted ${ids.length} action item${ids.length === 1 ? '' : 's'} into the plan.`,
      );
      setSelected(new Set());
    });

  const handleAcceptSelected = () =>
    runAction(async () => {
      const ids = Array.from(selected);
      if (ids.length === 0) return;
      await transcriptsApi.accept(transcript.id, ids);
      setSuccess(
        `Accepted ${ids.length} action item${ids.length === 1 ? '' : 's'} into the plan.`,
      );
      setSelected(new Set());
    });

  const handleAssignConfirm = async (
    targetExecutiveUserId?: string,
    projectId?: string,
  ) => {
    if (!assignTargetId) return;
    await transcriptsApi.assign(transcript.id, {
      extractionIds: [assignTargetId],
      targetExecutiveUserId,
      projectId,
    });
    setSuccess('Action assigned and added to the plan.');
    setAssignTargetId(null);
    setSelected((s) => {
      const next = new Set(s);
      next.delete(assignTargetId);
      return next;
    });
    await onChanged();
  };

  const assignTarget = assignTargetId
    ? extractions.find((e) => e.id === assignTargetId) ?? null
    : null;

  return (
    <Card sx={{ borderLeft: 3, borderColor: statusStyle.color }}>
      <CardContent>
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ mb: 1.5 }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
              <Typography variant="h6">{transcript.title}</Typography>
              <Chip
                label={transcript.status}
                size="small"
                sx={{ bgcolor: statusStyle.bg, color: statusStyle.color, fontWeight: 600 }}
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Typography variant="body2" color="text.secondary">
                {new Date(transcript.meetingDate).toLocaleDateString('en-GB', {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })}
              </Typography>
              {transcript.attendees && (
                <Typography variant="body2" color="text.secondary">
                  {transcript.attendees}
                </Typography>
              )}
              {transcript.tokensUsed != null && (
                <Typography variant="caption" color="text.secondary">
                  {transcript.tokensUsed.toLocaleString()} tokens
                </Typography>
              )}
            </Stack>
          </Box>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Tabs: extractions first, transcript second */}
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab
            value="extractions"
            label={`${transcriptsCopy.tabExtractions}${extractions.length > 0 ? ` (${extractions.length})` : ''}`}
          />
          <Tab value="transcript" label={transcriptsCopy.tabTranscript} />
        </Tabs>

        {tab === 'extractions' && (
          <>
            {!isAnalysed ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                {transcript.status === 'Processing'
                  ? 'Processing — extractions will appear shortly.'
                  : transcript.status === 'Error'
                  ? `Processing failed${transcript.errorMessage ? `: ${transcript.errorMessage}` : '.'}`
                  : 'Awaiting analysis.'}
              </Typography>
            ) : extractions.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, fontStyle: 'italic' }}>
                {transcriptsCopy.noExtractionsFound}
              </Typography>
            ) : (
              <Stack spacing={2}>
                {/* Batch controls + dismissed toggle */}
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems={{ sm: 'center' }}
                  justifyContent="space-between"
                  spacing={1.5}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={showDismissed}
                        onChange={(e) => setShowDismissed(e.target.checked)}
                      />
                    }
                    label={
                      <Typography variant="caption" color="text.secondary">
                        {transcriptsCopy.toggleShowDismissed}
                        {dismissedCount > 0 ? ` (${dismissedCount})` : ''}
                      </Typography>
                    }
                  />
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      startIcon={
                        mutating ? <CircularProgress size={14} color="inherit" /> : <AcceptIcon />
                      }
                      onClick={handleAcceptSelected}
                      disabled={selected.size === 0 || mutating}
                    >
                      {transcriptsCopy.batchAcceptSelected} ({selected.size})
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={
                        mutating ? <CircularProgress size={14} color="inherit" /> : <AcceptIcon />
                      }
                      onClick={handleAcceptAll}
                      disabled={!hasPending || mutating}
                    >
                      {transcriptsCopy.batchAcceptAll} ({pending.length})
                    </Button>
                  </Stack>
                </Stack>

                {/* Extraction cards */}
                <Stack spacing={1}>
                  {visible.map((ext) => (
                    <ExtractionCard
                      key={ext.id}
                      extraction={ext}
                      selected={selected.has(ext.id)}
                      onToggleSelect={() => toggleSelect(ext.id)}
                      onAccept={() => handleAcceptOne(ext)}
                      onAssign={() => setAssignTargetId(ext.id)}
                      onDismiss={() => handleDismissOne(ext)}
                      disabled={mutating}
                    />
                  ))}
                </Stack>

                {/* All-handled state when nothing is pending and hidden-dismissed
                    don't count either. */}
                {!hasPending && !showDismissed && (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="subtitle2">
                      {transcriptsCopy.allHandledHeading}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {transcriptsCopy.allHandledBody}
                    </Typography>
                  </Box>
                )}
              </Stack>
            )}
          </>
        )}

        {tab === 'transcript' && (
          <Stack spacing={2}>
            {transcript.aiSummary && (
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'rgba(108, 155, 255, 0.06)',
                  borderRadius: 2,
                  border: '1px solid rgba(108, 155, 255, 0.1)',
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                  AI summary
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                  {transcript.aiSummary}
                </Typography>
              </Box>
            )}
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                maxHeight: 480,
                overflowY: 'auto',
              }}
            >
              <Typography
                variant="body2"
                component="pre"
                sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', m: 0 }}
              >
                {transcript.transcriptText}
              </Typography>
            </Box>
          </Stack>
        )}

        <AssignDialog
          open={assignTargetId !== null}
          onClose={() => setAssignTargetId(null)}
          onConfirm={handleAssignConfirm}
          extractionTitle={assignTarget?.title}
        />
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function TranscriptsPage() {
  const [transcripts, setTranscripts] = useState<MeetingTranscript[]>([]);
  const [loading, setLoading] = useState(true);

  const loadTranscripts = useCallback(async () => {
    try {
      const data = await transcriptsApi.getAll();
      setTranscripts(data);
    } catch (err) {
      console.error('Failed to load transcripts', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTranscripts();
  }, [loadTranscripts]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <TranscriptIcon sx={{ color: 'primary.main', fontSize: 28 }} />
        <Typography variant="h4">Transcripts</Typography>
      </Box>
      <Typography variant="subtitle1" sx={{ mb: 3 }}>
        Upload meeting transcripts and review the extracted action items.
      </Typography>

      <UploadForm onUploaded={loadTranscripts} />

      <Typography variant="h5" sx={{ mb: 2 }}>
        Past transcripts
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && transcripts.length === 0 && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <TranscriptIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No transcripts yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Upload your first meeting transcript above to get started.
            </Typography>
          </CardContent>
        </Card>
      )}

      <Stack spacing={2}>
        {transcripts.map((t) => (
          <TranscriptCard key={t.id} transcript={t} onChanged={loadTranscripts} />
        ))}
      </Stack>
    </Box>
  );
}

export default TranscriptsPage;
